import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface ShippingMethod {
    id: number;
    code: string;
    name: string;
    localizations: Localization[];
}

export async function getShippingMethod(id: number): Promise<ShippingMethod> {
    const result = await rms2Fetch<ShippingMethod>(`shippingmethods/${id}`);
    if (!result) throw new Error('Empty response for getShippingMethod');
    return result;
}

export async function createShippingMethod(sm: Partial<ShippingMethod>): Promise<ShippingMethod> {
    const result = await rms2Fetch<ShippingMethod>('shippingmethods', {
        method: 'POST',
        body: JSON.stringify({
            code: sm.code,
            name: sm.name,
            localizations: sm.localizations?.map((l) => ({ languageId: l.language.id, name: l.name }))
        })
    });
    if (!result) throw new Error('Empty response for createShippingMethod');
    return result;
}

export async function updateShippingMethod(sm: ShippingMethod): Promise<ShippingMethod> {
    const result = await rms2Fetch<ShippingMethod>(`shippingmethods/${sm.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            code: sm.code,
            name: sm.name,
            localizations: sm.localizations?.map((l) => ({ languageId: l.language.id, name: l.name }))
        })
    });
    if (!result) throw new Error('Empty response for updateShippingMethod');
    return result;
}

export async function deleteShippingMethod(id: number): Promise<void> {
    await rms2Fetch<void>(`shippingmethods/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    const response = await rms2Fetch<{ languages: Language[] }>('languages');
    return response?.languages ?? [];
}
